/* Allocation & Billing — printable invoice.
   Mirrors the CureBay Medishop E-Clinic invoice PDF layout: header (logo,
   business + GSTIN block, bill meta), patient row, line items grouped per
   allocated lot, then totals on the right and terms on the left.
   Opens the same kind of print window the rest of the app uses
   (see ShipmentBarcode.jsx). */
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  getResolvedWarehouseCode,
  getWarehouseInfo,
} from "../../Services/warehouseContext";
import { INVOICE_LOGO_DATA_URI } from "./invoiceLogo";
import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";

/* Fallback pharmacy block — used when no warehouse is resolved (e.g. an old
   session that pre-dates login-based resolution). */
const DEFAULT_PHARMACY = {
  name: "CureBay Medishop",
  sub: "(A unit of CureBay Pharmacy Pvt. Ltd.)",
  address:
    "Plot No-432, Khata No-428, Po-BOMIKHAL, Unit-12, PS-SAHEED NAGAR, BHUBANESWAR – 751010, Odisha",
  cityName: "Bhubaneswar",
  pincode: "751010",
  stateCode: "21",
  stateName: "Odisha",
  dl: "KH-BBS-III-44171-R / 44172-RC-19100-RX",
  gstin: "21AAKCC7552J1ZK",
  pan: "AAKCC7552J",
};

/* Empty / null fields render as an em dash on the invoice. */
const dash = (v) => (v == null || v === "" ? "—" : v);

/* GST state code is the first two digits of the GSTIN. */
const stateCodeFromGstin = (gstin) => {
  const m = String(gstin || "").match(/^(\d{2})/);
  return m ? m[1] : "";
};

/* Build the invoice header pharmacy block from the login-resolved warehouse
   (Login.jsx -> localStorage). `sub` is a static tagline (not in warehouse/list).
   Falls back to DEFAULT_PHARMACY when nothing is resolved. */
function resolvePharmacy() {
  const info = getWarehouseInfo();
  if (!info) return DEFAULT_PHARMACY;
  const gstin = dash(info.gstin);
  return {
    name: dash(info.pharmacyName),
    sub: DEFAULT_PHARMACY.sub,
    address: dash(info.address),
    cityName: dash(info.cityName),
    pincode: info.pincode || "",
    stateName: dash(info.stateName),
    stateCode: stateCodeFromGstin(info.gstin) || info.stateCode || "",
    dl: dash(info.dl),
    gstin,
    pan: dash(info.pan),
  };
}

const pad = (n) => String(n).padStart(2, "0");

const formatExpMMYY = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})/);
  if (!m) return iso;
  return `${m[2]}/${m[1].slice(-2)}`;
};

const generateBillNo = (orderId) => {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(-2);
  const mmdd = `${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const tail = String(orderId || Date.now()).slice(-6);
  return `${yy}${mmdd}SC${tail}`;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v, digits = 2) => num(v).toFixed(digits);

const round2 = (n) => Math.round((num(n) + Number.EPSILON) * 100) / 100;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* "07 June 2026 | 17:20 hrs" — best effort across Date objects, ISO strings,
   and the app's "DD-MM-YYYY hh:mm AM/PM" strings; falls back to the raw input. */
const formatLongDateTime = (input) => {
  let d = input instanceof Date ? input : null;
  if (!d) {
    const s = String(input || "");
    d = new Date(s.replace(" ", "T"));
    if (isNaN(d.getTime())) {
      const m = s.match(
        /(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})\s*(AM|PM)?)?/i,
      );
      if (m) {
        let hh = Number(m[4] || 0);
        if (/PM/i.test(m[6] || "") && hh < 12) hh += 12;
        if (/AM/i.test(m[6] || "") && hh === 12) hh = 0;
        /* These app strings are IST wall-clock — pin the +05:30 offset so the
           instant is correct regardless of where this code runs. */
        d = new Date(
          `${m[3]}-${m[2]}-${m[1]}T${pad(hh)}:${pad(Number(m[5] || 0))}:00+05:30`,
        );
      }
    }
  }
  if (!d || isNaN(d.getTime())) return String(input || "");
  /* Always render in IST so the printed time matches the pharmacy's clock even
     when the host/browser runs in another timezone (e.g. a UTC server). */
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .formatToParts(d)
    .reduce((a, part) => {
      a[part.type] = part.value;
      return a;
    }, {});
  return `${p.day} ${MONTHS[Number(p.month) - 1]} ${p.year} | ${p.hour}:${p.minute} hrs`;
};

/* Indian number-to-words (used for the invoice "Amount in Words" line). */
const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

const belowHundred = (n) =>
  n < 20
    ? ONES[n]
    : `${TENS[Math.floor(n / 10)]}${n % 10 ? " " + ONES[n % 10] : ""}`;

const belowThousand = (n) => {
  if (n < 100) return belowHundred(n);
  const r = n % 100;
  return `${ONES[Math.floor(n / 100)]} Hundred${r ? " " + belowHundred(r) : ""}`;
};

const numToWords = (n) => {
  if (n <= 0) return "Zero";
  const out = [];
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  if (crore) out.push(`${numToWords(crore)} Crore`);
  if (lakh) out.push(`${belowHundred(lakh)} Lakh`);
  if (thousand) out.push(`${belowThousand(thousand)} Thousand`);
  if (n) out.push(belowThousand(n));
  return out.join(" ");
};

const amountInWords = (amount) => {
  const rupees = Math.floor(num(amount));
  const paise = Math.round((num(amount) - rupees) * 100);
  let str = `${rupees > 0 ? numToWords(rupees) : "Zero"} Rupees`;
  if (paise > 0) str += ` and ${numToWords(paise)} Paise`;
  return `${str} Only`;
};

/* Friendly label for a backend payment-mode enum (E_CLINIC_ONLINE, B2C_ONLINE,
   …). Falls back to the raw value (already-friendly labels pass through) or a
   sensible default. */
const PAYMENT_MODE_LABELS = {
  E_CLINIC_ONLINE: "E-Clinic (Online)",
  B2C_ONLINE: "B2C (Online)",
  COUNTER: "Counter Sale",
  COUNTER_SALE: "Counter Sale",
  CASH: "Cash",
  CARD: "Card",
  UPI: "UPI",
  NET_BANKING: "Net banking",
  CREDIT: "Credit (on account)",
  CUSTOMER_CREDIT: "Store credit",
  ONLINE: "Online",
};
const paymentModeLabel = (mode) => {
  if (!mode) return "Retail (Online)";
  const key = String(mode)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return PAYMENT_MODE_LABELS[key] || mode;
};

/* Enrich the flat item rows with GST math and roll up the invoice totals +
   per-HSN GST summary. MRP is treated as tax-inclusive, so:
     taxable = net / (1 + rate%) ; CGST = SGST = (net - taxable) / 2.  */
const computeInvoiceMath = (rawItems) => {
  const items = rawItems.map((it) => {
    const rate = num(it.gstPct);
    const mrpTotal = round2(it.mrp * it.qty);
    /* Per-line "Disc." column = the MRP -> selling markdown only. Membership /
       CureCoin are folded into `amount` (the line net) by the builders and are
       shown as their own total lines below, so they DO reduce Net Payable. */
    const disc = round2(num(it.prodDisc));
    const netAmt = round2(it.amount);
    const taxable = round2(netAmt / (1 + rate / 100));
    const halfTax = round2((netAmt - taxable) / 2);
    return {
      ...it,
      gstPct: rate,
      mrpTotal,
      disc,
      netAmt,
      taxable,
      cgst: halfTax,
      sgst: halfTax,
    };
  });

  const sum = (f) => round2(items.reduce((s, i) => s + num(f(i)), 0));
  const netItems = sum((i) => i.netAmt);
  const netPayable = Math.round(netItems);

  /* One GST-summary row per HSN — if the same HSN appears on several lines they
     merge into a single row. The rate is the shared line rate; only if lines
     under one HSN somehow carry different rates is it derived from pooled tax. */
  const groups = new Map();
  items.forEach((i) => {
    const key = `${i.hsn}`;
    const g = groups.get(key) || {
      hsn: i.hsn,
      rate: i.gstPct,
      taxable: 0,
      cgst: 0,
      sgst: 0,
      mixed: false,
    };
    if (g.rate !== i.gstPct) g.mixed = true;
    g.taxable = round2(g.taxable + i.taxable);
    g.cgst = round2(g.cgst + i.cgst);
    g.sgst = round2(g.sgst + i.sgst);
    groups.set(key, g);
  });
  groups.forEach((g) => {
    if (g.mixed && g.taxable > 0)
      g.rate = Math.round(((g.cgst + g.sgst) / g.taxable) * 100);
    delete g.mixed;
  });

  const productDiscount = sum((i) => i.prodDisc);
  const membershipDiscount = sum((i) => i.membershipDisc);
  const cureCoinDiscount = sum((i) => i.cureCoinDisc);
  const totals = {
    itemCount: items.length,
    totalMrp: sum((i) => i.mrpTotal),
    productDiscount,
    membershipDiscount,
    cureCoinDiscount,
    /* Net Discount = every discount shown above it (product + membership +
       CureCoin), so it isn't 0 when the only saving is a CureCoin/membership
       discount rather than an MRP->selling product discount. */
    netDiscount: round2(
      productDiscount + membershipDiscount + cureCoinDiscount,
    ),
    taxableValue: sum((i) => i.taxable),
    cgst: sum((i) => i.cgst),
    sgst: sum((i) => i.sgst),
    roundOff: round2(netPayable - netItems),
    netPayable,
  };
  return { items, totals, gstSummary: [...groups.values()] };
};

/* Flatten allocations into one invoice row per (medicine, lot). */
const buildItems = ({ lineStatus, alloc, lots }) => {
  const items = [];
  lineStatus.forEach((line) => {
    /* `alloc` is now keyed per-line (lineKey). Fall back to drugCode for any
       caller that still uses the old shape so this stays compatible. */
    const picks = alloc[line.lineKey] || alloc[line.drugCode] || {};
    Object.entries(picks).forEach(([lotId, takenQtyRaw]) => {
      const qty = num(takenQtyRaw);
      if (qty <= 0) return;
      const lot = lots.find((L) => String(L.id) === String(lotId));
      if (!lot) return;
      const mrp = num(lot.mrp);
      const sale = num(lot.sale);
      const prodDisc = Math.max(0, (mrp - sale) * qty);
      /* Membership & CureCoin are REAL reductions to what the customer pays —
         they lower the line net (and so its taxable value, GST and the bill's
         Net Payable), exactly like the counter-sale path (computeBill). */
      const membershipDisc = line.membershipDiscountedPrice
        ? Math.max(
            0,
            (num(line.mrp) - num(line.membershipDiscountedPrice)) * qty,
          )
        : 0;
      const cureCoinDisc = num(line.cureCoinDiscount);
      const amount = round2(
        Math.max(0, sale * qty - membershipDisc - cureCoinDisc),
      );
      items.push({
        sno: items.length + 1,
        name: line.drugName || "",
        schedule: line.schedule || line.scheduleType || line.drugSchedule || "",
        /* HSN and GST are per-batch (fromInventoryRow) — take them from the
           allocated lot first, then the order line. No stand-in code: printing
           30049099 for a batch that has no HSN puts a number on a tax invoice
           that nobody entered and that may not describe the goods. Blank is
           visible and gets fixed; a plausible default does not. */
        hsn: lot.hsnCode || line.hsn || line.hsnCode || "",
        pack: line.drugsInfoTypeofsell || "",
        batch: lot.batch || "",
        exp: formatExpMMYY(lot.exp),
        gstPct: num(lot.gstPct) || num(line.gstPct ?? line.taxPct) || 5,
        mrp,
        prodDisc,
        /* The displayed Membership Discount is the ₹ saving (line MRP minus the
           membership price); `membershipDiscount` (a %) is carried separately
           for the payload. CureCoin is a ₹ amount given directly on the line.
           Both are already folded into `amount` above so they reduce Net Payable. */
        membershipDisc,
        cureCoinDisc,
        qty,
        /* Total ordered quantity for this order line (not the per-lot pick).
           Sent as `quantity` in the v1/sales payload; `fulfilledQuantity`
           carries `qty` — how much we are allocating now. */
        lineQty: num(line.qty),
        amount,
        /* carried through for the v1/sales update payload, not rendered */
        inventoryId: Number(lot.id),
        medicineCode: line.drugCode,
        orderDetailsId: line.id ?? line.orderDetailsId ?? 0,
        discountPct: num(line.discountPercentage ?? line.discountPct ?? 0),
        membershipDiscount: num(line.membershipDiscount),
        /* Order-line discount figures, passed straight through to the
           v1/sales payload (no client proration). */
        discountAmount: num(line.discountAmount),
        cureCoinDiscount: num(line.cureCoinDiscount),
      });
    });
  });
  return items;
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const orderQrDataUri = (value) => {
  if (!value) return "";
  const svg = renderToStaticMarkup(
    createElement(QRCodeSVG, {
      value: String(value),
      size: 128,
      level: "M",
      marginSize: 1,
    }),
  );
  const withNs = svg.replace(
    "<svg ",
    '<svg xmlns="http://www.w3.org/2000/svg" ',
  );
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(withNs)}`;
};

/* Schedule sub-label under an item name. Prescription schedules (H/H1/X) get
   the "Rx Required" hint. */
const scheduleLabel = (schedule) => {
  if (!schedule) return "";
  const s = String(schedule).toUpperCase();
  const rx = ["H", "H1", "X"].includes(s) ? " — Rx Required" : "";
  return `Schedule ${escapeHtml(schedule)}${rx}`;
};

const buildHTML = ({
  pharmacy = DEFAULT_PHARMACY,
  patient = {},
  shipTo = {},
  meta = {},
  items,
  totals,
  gstSummary = [],
  amountWords = "",
}) => {
  const hasVal = (v) => v != null && v !== "" && v !== "—";
  /* Ship To falls back to the patient (direct B2C ships to the buyer). */
  const shipName = hasVal(shipTo.name) ? shipTo.name : patient.name;
  const shipAddr = hasVal(shipTo.address) ? shipTo.address : patient.address;
  const shipMobile = hasVal(shipTo.mobile) ? shipTo.mobile : patient.mobile;
  /* CGST/SGST column + total labels carry no rate — the actual per-HSN rates are
     shown dynamically (from each line's GST) in the GST Summary table below. */
  const taxColLabel = (tax) => tax;

  const itemsHTML = items
    .map((it) => {
      const sub = scheduleLabel(it.schedule);
      return `
        <tr>
          <td class="c">${it.sno}</td>
          <td><div class="it-name">${escapeHtml(it.name)}</div>${
            sub ? `<div class="it-sub">${sub}</div>` : ""
          }</td>
          <td class="c">${escapeHtml(it.hsn)}</td>
          <td class="c">${escapeHtml(it.batch)}</td>
          <td class="c">${escapeHtml(it.exp)}</td>
          <td class="r">${fmt(it.mrp)}</td>
          <td class="c">${fmt(it.qty, 0)}</td>
          <td class="r">${fmt(it.mrpTotal)}</td>
          <td class="r">${fmt(it.disc)}</td>
          <td class="r">${fmt(it.taxable)}</td>
          <td class="r">${fmt(it.cgst)}</td>
          <td class="r">${fmt(it.sgst)}</td>
          <td class="r">${fmt(0)}</td>
          <td class="r net">${fmt(it.netAmt)}</td>
        </tr>`;
    })
    .join("");

  const gstHTML = gstSummary
    .map(
      (g) => `
        <tr>
          <td class="c">${escapeHtml(g.hsn)}</td>
          <td class="c">${g.rate}% (CGST ${g.rate / 2}% + SGST ${g.rate / 2}%)</td>
          <td class="r">${fmt(g.taxable)}</td>
          <td class="r">${fmt(g.cgst)}</td>
          <td class="r">${fmt(g.sgst)}</td>
          <td class="r">${fmt(0)}</td>
          <td class="r">${fmt(g.cgst + g.sgst)}</td>
        </tr>`,
    )
    .join("");

  const qrValue = meta.orderId || meta.orderRef || "";
  const qrSrc = orderQrDataUri(qrValue);

  return `
<table class="hdr"><tbody><tr>
  <td class="hdr-left">
    <div class="brand-logo"><img src="${INVOICE_LOGO_DATA_URI}" alt="${escapeHtml(
      pharmacy.name,
    )}" width="210" height="38"/></div>
    <div class="biz-name">${escapeHtml(pharmacy.name)}</div>
    <div class="biz-sub">${escapeHtml(pharmacy.sub)}</div>
    <div class="biz-addr">${escapeHtml(pharmacy.address)}</div>
    <div class="biz-meta">DL No : ${escapeHtml(pharmacy.dl)}</div>
    <div class="biz-meta">GSTIN : ${escapeHtml(pharmacy.gstin)}</div>
    <div class="biz-meta">PAN : ${escapeHtml(pharmacy.pan)} | State Code : ${escapeHtml(
      pharmacy.stateCode,
    )} (${escapeHtml(pharmacy.stateName)})</div>
  </td>
  <td class="hdr-right">
    <table class="inv-title-row"><tbody><tr>
      <td><div class="inv-title">TAX INVOICE</div></td>${
        qrSrc
          ? `
      <td class="inv-qr"><img src="${qrSrc}" alt="Order ID QR" width="64" height="64"/><div class="inv-qr-lbl">${escapeHtml(
        qrValue,
      )}</div></td>`
          : ""
      }
    </tr></tbody></table>
    <table class="meta-kv"><tbody>
      <tr><td>Invoice No.</td><td class="b">${escapeHtml(meta.invoiceNo)}</td></tr>
      <tr><td>Invoice Date</td><td class="b">${escapeHtml(meta.invoiceDateText)}</td></tr>
      <tr><td>Payment Mode</td><td class="b">${escapeHtml(meta.paymentMode)}</td></tr>
      <tr><td>Order Ref (OTA)</td><td class="b">#${escapeHtml(meta.orderRef)}</td></tr>${
        meta.transactionId
          ? `
      <tr><td>Transaction ID</td><td class="b">${escapeHtml(meta.transactionId)}</td></tr>`
          : ""
      }
      <tr><td>Place of Supply</td><td class="b">${escapeHtml(meta.placeOfSupply)}</td></tr>
    </tbody></table>
  </td>
</tr></tbody></table>

<table class="billship"><tbody><tr>
  <td class="bs-cell">
    <div class="bs-h">Bill To</div>
    <div class="bs-name">${escapeHtml(patient.name)}${
      hasVal(patient.id)
        ? ` <span class="bs-id">(ID: ${escapeHtml(patient.id)})</span>`
        : ""
    }</div>${
      hasVal(patient.address)
        ? `
    <div class="bs-addr">${escapeHtml(patient.address)}</div>`
        : ""
    }
    <div class="bs-line"><span class="lbl">Mobile :</span> <span class="pv">${escapeHtml(
      patient.mobile,
    )}</span></div>
    <div class="bs-line"><span class="lbl">Age / Gender :</span> <span class="pv">${escapeHtml(
      patient.age,
    )} / ${escapeHtml(patient.gender)}</span></div>
  </td>
  <td class="bs-cell bs-ship">
    <div class="bs-h">Ship To</div>
    <div class="bs-name">${escapeHtml(shipName)}</div>${
      hasVal(shipAddr)
        ? `
    <div class="bs-addr">${escapeHtml(shipAddr)}</div>`
        : ""
    }${
      hasVal(shipMobile)
        ? `
    <div class="bs-line"><span class="lbl">Mobile :</span> <span class="pv">${escapeHtml(
      shipMobile,
    )}</span></div>`
        : ""
    }
  </td>
</tr></tbody></table>

<table class="items">
  <thead>
    <tr>
      <th class="c">S. No</th>
      <th>Item Name</th>
      <th class="c">HSN Code</th>
      <th class="c">Batch No.</th>
      <th class="c">Expiry</th>
      <th class="r">MRP / Unit (₹)</th>
      <th class="c">Qty</th>
      <th class="r">MRP Total (₹)</th>
      <th class="r">Disc. (₹)</th>
      <th class="r">Taxable Value (₹)</th>
      <th class="r">${taxColLabel("CGST")} (₹)</th>
      <th class="r">${taxColLabel("SGST")} (₹)</th>
      <th class="r">${taxColLabel("IGST")} (₹)</th>
      <th class="r">Net Amt (₹)</th>
    </tr>
  </thead>
  <tbody>${itemsHTML}</tbody>
</table>

<table class="foot"><tbody><tr>
  <td class="terms">
    <div class="terms-h">Terms &amp; Conditions</div>
    <ol class="terms-list">
      <li>Consult doctor before using medicine.</li>
      <li>Cold storage medicines cannot be returned once sold.</li>
      <li>Subject to ${escapeHtml(pharmacy.cityName)} jurisdiction only.</li>
      <li>Sealed non-Rx items returnable within 7 days with original bill.</li>
      <li>Prescription medicines (Schedule H / H1 / X) are dispensed only against a valid prescription as per D&amp;C Act, 1940.</li>
      <li>Prescription records retained for a minimum of 2 years.</li>
    </ol>
    <div class="terms-note">MRP is inclusive of all taxes. Net payable cannot exceed MRP.</div>
    <div class="terms-note">Reverse Charge Mechanism (RCM): No</div>
  </td>
  <td class="tot">
    <table class="tot-kv"><tbody>
      <tr><td>Total Items</td><td class="r b">${totals.itemCount}</td></tr>
      <tr><td>Total MRP (₹)</td><td class="r b">${fmt(totals.totalMrp)}</td></tr>
      <tr><td>Product Discount (₹)</td><td class="r b">${fmt(totals.productDiscount)}</td></tr>
      <tr><td>Membership Discount (₹)</td><td class="r b">${fmt(totals.membershipDiscount)}</td></tr>
      <tr><td>CureCoin Discount (₹)</td><td class="r b">${fmt(totals.cureCoinDiscount)}</td></tr>
      <tr><td>Net Discount (₹)</td><td class="r b">${fmt(totals.netDiscount)}</td></tr>
      <tr><td>Taxable Value (₹)</td><td class="r b">${fmt(totals.taxableValue)}</td></tr>
      <tr><td>CGST (₹)</td><td class="r b">${fmt(totals.cgst)}</td></tr>
      <tr><td>SGST (₹)</td><td class="r b">${fmt(totals.sgst)}</td></tr>
      <tr><td>IGST (₹)</td><td class="r b">${fmt(0)}</td></tr>
      <tr><td>Round Off (₹)</td><td class="r b">${fmt(totals.roundOff)}</td></tr>
    </tbody></table>
    <table class="net-bar"><tbody><tr><td>NET PAYABLE</td><td class="r">₹ ${fmt(
      totals.netPayable,
    )}</td></tr></tbody></table>
    <div class="words-lbl">Amount in Words:</div>
    <div class="words">${escapeHtml(amountWords)}</div>
    <div class="sign">
      <div class="sign-rule"></div>
      <div>Authorised Signatory</div>
      <div class="sign-strong">Pharmacist-in-Charge</div>
      <div class="sign-sub">(Reg. No. ___________)</div>
      <div class="sign-sub">Billed By: Pharmacist</div>
    </div>
  </td>
</tr></tbody></table>

<div class="gst-h">GST Summary</div>
<table class="gst">
  <thead>
    <tr>
      <th class="c">HSN Code</th>
      <th class="c">Applicable GST Rate</th>
      <th class="r">Taxable Value (₹)</th>
      <th class="r">${taxColLabel("CGST")} (₹)</th>
      <th class="r">${taxColLabel("SGST")} (₹)</th>
      <th class="r">${taxColLabel("IGST")} (₹)</th>
      <th class="r">Total Tax (₹)</th>
    </tr>
  </thead>
  <tbody>
    ${gstHTML}
    <tr class="gst-total">
      <td class="c b">Total</td>
      <td></td>
      <td class="r b">${fmt(totals.taxableValue)}</td>
      <td class="r b">${fmt(totals.cgst)}</td>
      <td class="r b">${fmt(totals.sgst)}</td>
      <td class="r b">${fmt(0)}</td>
      <td class="r b">${fmt(totals.cgst + totals.sgst)}</td>
    </tr>
  </tbody>
</table>

<div class="compliance"><strong>Compliance Note:</strong> System-generated Tax Invoice under CGST Act, 2017. Prescription medicines (Schedule H / H1 / X) are dispensed only against a valid prescription as per the Drugs &amp; Cosmetics Act, 1940; records are retained for a minimum of 2 years. Drug Licence valid under Rule 64. No return or exchange on prescription medicines once dispensed.</div>${
    qrSrc
      ? `
<div class="foot-qr"><img src="${qrSrc}" alt="Order ID QR" width="80" height="80"/><div class="foot-qr-lbl">Order ID: ${escapeHtml(
          qrValue,
        )}</div></div>`
      : ""
  }
`;
};

/* GST tax-invoice layout. Every column relationship is driven by a real
   <table>/<td> because html2canvas (used by html2pdf) does not reliably honour
   flex / table-cell on divs. Solid background colours use print-color-adjust so
   the purple header bands survive both the print window and the PDF capture. */
/* Brand palette: navy accent for the header bands / titles / table headers, a
   green bar for NET PAYABLE. ACCENT_DARK is used for the patient values. */
const ACCENT = "#0B2545"; // navy
const ACCENT_DARK = "#081a33"; // darker navy
const ACCENT_GREEN = "#4CAF50"; // green — NET PAYABLE bar
const STYLES = `
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  /* Fixed content width so the layout (column wrapping, line breaks) is
     identical in the on-screen preview and the captured PDF — the upload is
     then an exact copy of what the operator sees, regardless of iframe size. */
  body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; padding: 24px; font-size: 11px; width: 780px; margin: 0 auto; }
  table { border-collapse: collapse; }
  td, th { vertical-align: top; }
  .r { text-align: right; }
  .c { text-align: center; }
  .b { font-weight: 700; }

  /* Header */
  table.hdr { width: 100%; table-layout: fixed; margin-bottom: 10px; }
  table.hdr > tbody > tr > td.hdr-left { padding-right: 24px; }
  table.hdr > tbody > tr > td.hdr-right { width: 280px; }
  .brand-logo { margin-bottom: 6px; }
  .brand-logo img { display: block; width: 210px; height: 38px; }
  .biz-name { font-weight: 800; font-size: 20px; color: ${ACCENT}; letter-spacing: 0.2px; }
  .biz-sub { font-weight: 700; font-size: 11px; margin-top: 2px; }
  .biz-addr { font-size: 10.5px; line-height: 1.5; margin-top: 6px; color: #444; max-width: 460px; }
  .biz-meta { font-size: 10px; margin-top: 2px; color: #333; }
  .inv-title { font-weight: 800; font-size: 22px; color: ${ACCENT}; margin-bottom: 8px; }
  table.meta-kv { width: 100%; font-size: 10.5px; }
  table.meta-kv td { padding: 2px 0; color: #555; }
  table.meta-kv td.b { color: #1a1a1a; text-align: left; padding-left: 12px; }

  /* Bill To / Ship To band — two equal columns, fixed layout so the divider
     and the two blocks line up exactly regardless of content length. */
  table.billship { width: 100%; margin: 8px 0; table-layout: fixed; border: 1px solid #e3dcef; }
  table.billship td.bs-cell { width: 50%; padding: 8px 12px; font-size: 10.5px; vertical-align: top; }
  table.billship td.bs-ship { border-left: 1px solid #e3dcef; }
  .bs-h { font-weight: 700; color: ${ACCENT}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
  .bs-name { font-weight: 700; color: ${ACCENT_DARK}; font-size: 11px; }
  .bs-id { font-weight: 400; color: #666; }
  .bs-addr { color: #444; line-height: 1.5; margin-top: 3px; }
  .bs-line { margin-top: 3px; }
  table.billship .lbl { color: #666; }
  table.billship .pv { font-weight: 700; color: ${ACCENT_DARK}; }

  /* Items */
  table.items { width: 100%; margin-top: 6px; table-layout: auto; }
  table.items th { background: ${ACCENT}; color: #fff; font-weight: 700; font-size: 9.5px; padding: 7px 5px; text-align: left; line-height: 1.2; }
  /* All item-table values left-aligned (overrides the .c/.r helper classes on
     the cells via higher specificity). */
  table.items td { padding: 7px 5px; font-size: 10px; border-bottom: 1px solid #eee; text-align: left; }
  table.items tbody tr:nth-child(even) { background: #f7f4fb; }
  .it-name { font-weight: 600; }
  .it-sub { font-size: 9px; color: ${ACCENT}; margin-top: 2px; }
  table.items td.net { font-weight: 700; }

  /* Footer: terms + totals */
  table.foot { width: 100%; margin-top: 14px; table-layout: fixed; }
  table.foot > tbody > tr > td.terms { font-size: 10px; line-height: 1.5; padding-right: 24px; vertical-align: top; }
  .terms-h { font-weight: 700; color: ${ACCENT}; margin-bottom: 4px; }
  ol.terms-list { margin: 0; padding-left: 16px; }
  ol.terms-list li { margin-bottom: 2px; }
  .terms-note { color: ${ACCENT}; margin-top: 8px; }
  table.foot > tbody > tr > td.tot { width: 300px; vertical-align: top; }
  table.tot-kv { width: 100%; font-size: 10.5px; }
  table.tot-kv td { padding: 3px 0; border-bottom: 1px solid #f0ecf6; }
  table.net-bar { width: 100%; background: ${ACCENT_GREEN}; color: #fff; margin-top: 8px; table-layout: fixed; }
  table.net-bar td { padding: 9px 12px; font-weight: 800; font-size: 14px; color: #fff; }
  .words-lbl { font-size: 10px; color: #666; margin-top: 10px; }
  .words { font-weight: 700; font-size: 10.5px; }
  .sign { margin-top: 22px; text-align: center; font-size: 10px; }
  .sign-rule { border-top: 1px solid #999; margin: 0 auto 4px; width: 80%; }
  .sign-strong { font-weight: 700; margin-top: 16px; }
  .sign-sub { color: #666; }

  /* GST summary */
  .gst-h { font-weight: 700; color: ${ACCENT}; font-size: 11px; margin: 18px 0 6px; }
  table.gst { width: 100%; table-layout: auto; }
  table.gst th { background: ${ACCENT}; color: #fff; font-weight: 700; font-size: 9.5px; padding: 7px 6px; text-align: left; }
  table.gst td { padding: 7px 6px; font-size: 10px; border-bottom: 1px solid #eee; }
  table.gst tr.gst-total td { background: #f2eef8; border-bottom: none; }

  table.inv-title-row { width: 100%; margin-bottom: 4px; }
  table.inv-title-row td { vertical-align: top; padding: 0; }
  td.inv-qr { width: 70px; text-align: center; }
  td.inv-qr img { display: block; width: 64px; height: 64px; margin: 0 auto; }
  .inv-qr-lbl { font-size: 8px; color: #555; margin-top: 2px; word-break: break-all; }
  .foot-qr { margin-top: 10px; text-align: center; break-inside: avoid; page-break-inside: avoid; }
  .foot-qr img { display: block; width: 80px; height: 80px; margin: 0 auto; }
  .foot-qr-lbl { font-size: 9px; color: #555; margin-top: 3px; }
  .compliance { margin-top: 16px; padding-top: 10px; border-top: 1px solid #e3dcef; font-size: 9px; color: #777; line-height: 1.5; }

  /* Print to a single A4 portrait page. The fixed 780px body (≈206mm) is wider
     than A4's printable area, so on print we drop the fixed width/padding and let
     the content fill the @page content box; break-inside guards stop tables and
     the totals/GST blocks from being sliced across a page boundary. */
  @page { size: A4 portrait; margin: 8mm; }
  @media print {
    html, body { margin: 0; }
    body { width: 100%; padding: 0; }
    table.items tr, table.gst tr { break-inside: avoid; page-break-inside: avoid; }
    table.foot, table.net-bar, .gst-h, .compliance, .sign { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }
  }
`;

/* Build the full invoice payload (rendered HTML + the structured data that
   gets POSTed to v1/sales for "update" on print). Pure — no DOM side effects.
   Used by:
     - In-app preview modal (renders html via dangerouslySetInnerHTML)
     - openPrintWindow below (opens the same html in a print window)
     - handlePrint in AllocationBilling (sends `updatePayload` to the update API)

   `overrides` lets the caller pin specific header values (typically the
   invoiceNumber / invoiceDate / invoiceId returned by v1/sales after the
   bill is posted) so the preview shows the backend's authoritative bill no
   instead of the client-generated placeholder. */
export function buildInvoice({
  order,
  lineStatus,
  alloc,
  lots,
  totals: t,
  overrides = {},
  warehouseId = getResolvedWarehouseCode(),
  pharmacy = resolvePharmacy(),
}) {
  const raw = buildItems({ lineStatus, alloc, lots });
  if (raw.length === 0) return null;

  const { items, totals, gstSummary } = computeInvoiceMath(raw);

  /* Human-readable label for the invoice; `paymentModeCode` is the backend
     enum sent in the v1/sales payload. */
  const isB2C = order?.orderSource === "B2C";
  const paymentMode = isB2C ? "B2C (Online)" : "E-Clinic (Online)";
  const paymentModeCode = isB2C ? "B2C_ONLINE" : "E_CLINIC_ONLINE";
  const orderRef = (
    overrides.txnId ||
    order?.transactionId ||
    order?.orderId ||
    ""
  )
    .toString()
    .toUpperCase();
  const billNo = overrides.billNo || generateBillNo(order?.orderId);

  /* Bill To = patient's own address (address1-3); Ship To = the E-clinic
     delivery address (deliveryAddress1-3) with its branch name when the order
     came via an E-clinic, else it falls back to the patient in buildHTML. */
  const joinAddr = (...parts) => parts.filter(Boolean).join(", ");
  const patientAddr = joinAddr(
    order?.address1,
    order?.address2,
    order?.address3,
  );
  const eclinicAddr = joinAddr(
    order?.deliveryAddress1,
    order?.deliveryAddress2,
    order?.deliveryAddress3,
  );

  const patient = {
    id: order?.patientId || order?.patientCode || "—",
    name:
      `${order?.patientSalutation ? order.patientSalutation + " " : ""}${
        order?.patientName || ""
      }`
        .trim()
        .toUpperCase() || "—",
    mobile: order?.patientMobile || "—",
    age: order?.patientAge || order?.age || "—",
    gender: order?.patientGender || order?.gender || "—",
    address: patientAddr,
  };

  const shipTo = {
    name: order?.cheBranchName || "",
    address: eclinicAddr,
    mobile: "",
  };

  /* Payment transaction id (refTxnId on the order-list row). Shown as its own
     "Transaction ID" line on the invoice — distinct from the OTA order ref. */
  const transactionId = (
    overrides.txnId ||
    order?.refTxnId ||
    order?.transactionId ||
    ""
  ).toString();

  const meta = {
    invoiceNo: billNo,
    invoiceDateText: formatLongDateTime(overrides.billDateTime || new Date()),
    paymentMode,
    orderRef,
    orderId: String(order?.orderId || ""),
    transactionId,
    placeOfSupply: pharmacy.stateName
      ? `${pharmacy.stateName}${
          pharmacy.stateCode ? ` (State Code: ${pharmacy.stateCode})` : ""
        }`
      : "—",
  };

  const html = buildHTML({
    pharmacy,
    patient,
    shipTo,
    meta,
    items,
    totals,
    gstSummary,
    amountWords: amountInWords(totals.netPayable),
  });

  return {
    html,
    styles: STYLES,
    billNo,
    items,
    totals,
    /* Body for v1/sales (application/json) — fired on Post bill to create
       the invoice and move the order to Confirm orders, and reused on Print
       to mark the invoice as printed. */
    updatePayload: {
      warehouseId,
      orderId: String(order?.orderId || ""),
      /* Payment transaction id (refTxnId) + payment mode, carried through to
         the sales record so the invoice can be reprinted with them later. */
      transactionId,
      paymentMode: paymentModeCode,
      customerName:
        `${order?.patientSalutation ? order.patientSalutation + " " : ""}${
          order?.patientName || ""
        }`.trim(),
      customerPhone: order?.patientMobile || "",
      doctorName: order?.doctorName || order?.consultationDoctor || "",
      remarks: t?.partial ? "invoiced partially" : "invoiced fully",
      items: items.map((it, idx) => {
        const billedQty = Number(it.qty) || 0;
        const orderedQty = Number(it.lineQty) || 0;
        /* `discountAmount` / `cureCoinDiscount` on the order line cover the WHOLE
           ordered quantity, but this item is one lot pick of it. Two ways that
           goes wrong unprorated:
             · partial bill — 1 of 6 billed still carried all six units' discount,
               which can exceed the amount actually being invoiced;
             · a line split across two lots — each lot repeated the full line
               discount, so it was counted twice.
           Scale by the share of the line this pick represents; the shares across
           a fully-allocated line sum back to the original amount. Capped at 1 and
           falling back to the full amount when the ordered qty is unknown, so an
           over-allocation can never inflate the discount. */
        const share = orderedQty > 0 ? Math.min(1, billedQty / orderedQty) : 1;
        return {
          inventoryId: Number(it.inventoryId ?? it.lotId ?? idx + 1),
          medicineCode: it.medicineCode || "",
          batchNo: it.batch || "",
          /* quantity = total ordered qty for the line; fulfilledQuantity = how
             much we are allocating now (this lot pick). Falls back to the
             allocated qty when the line total is missing. */
          quantity: Number(it.lineQty ?? it.qty) || 0,
          orderDetailsId: Number(it.orderDetailsId ?? 0),
          fulfilledQuantity: billedQty,
          discountAmount: round2(num(it.discountAmount) * share),
          cureCoinDiscount: round2(num(it.cureCoinDiscount) * share),
        };
      }),
    },
  };
}

/* Render the invoice HTML to a PDF Blob via html2canvas + jsPDF.
   Uses an off-screen iframe so the HTML lives in its own document — `body`
   styles apply correctly, the `load` event tells us layout is committed, and
   html2canvas captures the iframe's body (cloning the whole iframe document,
   stylesheet included) so the PDF matches the on-screen preview. Returns
   Promise<File>.

   NOTE: we deliberately do NOT use html2pdf's `.from(body)` — it clones the
   body into the host document and drops the iframe's <head><style>, producing
   an unstyled PDF. */
/* Turn a failed document upload into something an operator can act on.
   axios reports a rejected upload as a bare "Network Error" — the server's 413 /
   500 for an oversized multipart body carries no CORS headers, so the browser
   never surfaces the status. Saying "network" sends people to check their wifi
   when the real cause is the file. */
export function describeDocumentUploadError(e, file) {
  const status = e?.response?.status;
  const serverMsg = e?.response?.data?.message;
  const kb = file?.size ? Math.round(file.size / 1024) : null;
  const tooBig = status === 413 || status === 500 || !status;
  if (tooBig && kb && kb > 900) {
    return `The invoice PDF (${kb} KB) was refused by the server, which accepts up to about 1 MB. The bill is saved and can be printed — only the stored copy failed.`;
  }
  return (
    serverMsg ||
    (status
      ? `Upload failed (HTTP ${status}).`
      : "The invoice PDF could not be uploaded.")
  );
}

/* "Dolo 650 Tablet (R-05)" — the shelf the line was picked from, printed next to
   the name. The bill doubles as a picking slip at the counter, and the rack is the
   one thing on it that tells someone where to walk. Absent rack, absent suffix:
   never print an empty bracket. */
const withRack = (name, rack) => {
  const code = String(rack || "").trim();
  return code ? `${name} (${code})` : name;
};

export function invoiceToPdfBlob({ html, styles, billNo }, { maxBytes } = {}) {
  return htmlToPdfBlob({
    html,
    styles,
    filename: `Invoice ${billNo}.pdf`,
    maxBytes,
  });
}

/* Generic HTML -> PDF File. Same off-screen-iframe + html2canvas + jsPDF
   pipeline the invoice uses (see the note above); shared so other printable
   documents (debit notes, etc.) render identically instead of reimplementing
   the iframe/pagination handling. */
export function htmlToPdfBlob({
  html,
  styles,
  filename = "document.pdf",
  /* Hard ceiling for the produced file. Default sits under the document endpoint's
     1MB multipart limit with room for the form envelope, so anything this returns
     can be uploaded. */
  maxBytes = 900 * 1024,
}) {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.style.cssText = [
      "position: fixed",
      "top: 0",
      "left: 0",
      "width: 800px",
      /* Give the iframe a generous initial height so the body has room to
         lay out; html2canvas reads the body's scrollHeight, not this. */
      "height: 1200px",
      "border: 0",
      "opacity: 0",
      "pointer-events: none",
      "z-index: -1",
    ].join(";");
    /* `srcdoc` is set after attach so we get a single, predictable `load`. */
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) document.body.removeChild(iframe);
    };

    iframe.onload = () => {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc || !doc.body) {
        cleanup();
        reject(new Error("Could not access iframe document"));
        return;
      }
      const body = doc.body;

      /* Wait for every <img> inside the iframe (the CureBay logo, etc.)
         to fully decode so html2canvas doesn't snapshot a half-loaded
         frame. Resolves on error too so a broken logo doesn't stall. */
      const imgs = Array.from(body.querySelectorAll("img"));
      const imgsReady = Promise.all(
        imgs.map((img) => {
          const settle = () =>
            img.decode ? img.decode().catch(() => {}) : Promise.resolve();
          if (img.complete && img.naturalWidth > 0) return settle();
          return new Promise((res) => {
            img.onload = () => settle().then(res);
            img.onerror = () => res();
          });
        }),
      );

      /* One more animation frame after assets are ready so any final layout
         (e.g. images altering the body height) is committed. */
      const settled = () =>
        new Promise((res) =>
          requestAnimationFrame(() => requestAnimationFrame(res)),
        );

      imgsReady
        .then(settled)
        /* Render with html2canvas directly (NOT html2pdf's .from(), which
           clones the body into the host document and drops the iframe's
           <head><style>, producing an unstyled PDF). html2canvas clones the
           whole iframe document — stylesheet included — into its own sandbox,
           so the capture matches the preview exactly. */
        .then(() => {
          /* Scale is a budget, not a constant. A 30-line bill is three times the
             height of a 5-line one, and at a fixed scale of 2 the canvas — and the
             JPEG cut from it — grew with every line. The upload endpoint rejects
             anything over 1MB (Spring's multipart default), and the rejection reads
             as a browser "Network Error", so a long bill failed to attach with no
             usable message. Cap the total pixels instead: short invoices still get
             scale 2, tall ones step down as far as 1.1, which stays legible for
             print because the source is text, not imagery. */
          const PIXEL_BUDGET = 2600 * 2600;
          const raw = body.scrollWidth * body.scrollHeight || 1;
          const scale = Math.max(
            1.1,
            Math.min(2, Math.sqrt(PIXEL_BUDGET / raw)),
          );
          return html2canvas(body, {
            scale,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
            windowWidth: body.scrollWidth,
            windowHeight: body.scrollHeight,
            scrollX: 0,
            scrollY: 0,
          });
        })
        .then((canvas) => {
          /* A throwaway doc, used only to read A4 page dimensions — build() below
             creates the real one (possibly more than once, at falling quality). */
          const pdf = new jsPDF({
            unit: "mm",
            format: "a4",
            orientation: "portrait",
          });
          const margin = 10;
          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();
          const imgW = pageW - margin * 2;
          const imgH = (canvas.height * imgW) / canvas.width;
          const usableH = pageH - margin * 2;

          /* Build the document from a source canvas at a given JPEG quality. Each
             page re-adds the same image shifted up by one page height; jsPDF stores
             the image once and references it per page, so pagination is close to
             free — the encoded image is what decides the file size. */
          const build = (src, quality) => {
            const doc = new jsPDF({
              unit: "mm",
              format: "a4",
              orientation: "portrait",
            });
            const imgData = src.toDataURL("image/jpeg", quality);
            const h = (src.height * imgW) / src.width;
            let position = margin;
            let heightLeft = h;
            doc.addImage(imgData, "JPEG", margin, position, imgW, h);
            heightLeft -= usableH;
            while (heightLeft > 0) {
              position -= usableH;
              doc.addPage();
              doc.addImage(imgData, "JPEG", margin, position, imgW, h);
              heightLeft -= usableH;
            }
            return doc.output("blob");
          };

          /* Redraw the capture at a fraction of its pixels. Quality alone has a
             floor — past roughly 0.4 a JPEG stops shrinking and just looks bad — and
             on a long invoice the pixel count, not the quality, is what makes the
             file big. Bytes fall with the square of this factor, so it is the lever
             that actually converges. */
          const rescale = (src, factor) => {
            const c = document.createElement("canvas");
            c.width = Math.max(1, Math.round(src.width * factor));
            c.height = Math.max(1, Math.round(src.height * factor));
            const ctx = c.getContext("2d");
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            /* White base: JPEG has no alpha, and without this a transparent capture
               encodes as black. */
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, c.width, c.height);
            ctx.drawImage(src, 0, 0, c.width, c.height);
            return c;
          };

          /* Walk down until the file actually fits. The ladder drops quality first
             (cheap, invisible at these levels) and only then resolution, one step at
             a time, stopping at the first size that fits. A 30-line bill lands in
             the first two or three rungs; the deep rungs exist so an unusually long
             invoice still produces an uploadable file rather than one the server
             will reject.

             The cap is deliberately under 1MB: the endpoint's multipart limit is
             1MB and the request also carries the form envelope and headers. */
          const LADDER = [
            [1, 0.85],
            [1, 0.7],
            [0.85, 0.7],
            [0.72, 0.65],
            [0.6, 0.6],
            [0.5, 0.55],
            [0.42, 0.5],
            [0.34, 0.45],
            [0.26, 0.4],
            [0.2, 0.35],
          ];
          let blob = null;
          let scaled = canvas;
          let lastFactor = 1;
          for (const [factor, quality] of LADDER) {
            if (factor !== lastFactor) {
              scaled = rescale(canvas, factor);
              lastFactor = factor;
            }
            blob = build(scaled, quality);
            if (blob.size <= maxBytes) break;
          }

          /* The ladder is sized for real invoices, not for every conceivable one, so
             it ends with a loop rather than a shrug: keep shrinking until the file
             fits. Each pass cuts the linear size by 30%, so bytes roughly halve, and
             the floor stops it at a width no longer worth encoding. Without this the
             function could hand back a file the server is certain to reject, which is
             the bug this whole ladder exists to prevent. */
          let factor = lastFactor;
          while (blob.size > maxBytes && scaled.width > 240) {
            factor *= 0.7;
            scaled = rescale(canvas, factor);
            blob = build(scaled, 0.4);
          }
          cleanup();
          resolve(new File([blob], filename, { type: "application/pdf" }));
        })
        .catch((e) => {
          cleanup();
          reject(e);
        });
    };

    iframe.onerror = (e) => {
      cleanup();
      reject(e instanceof Error ? e : new Error("iframe failed to load"));
    };

    iframe.srcdoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${styles}</style></head><body>${html}</body></html>`;
  });
}

export function openPrintWindow({ html, styles, billNo }) {
  const w = window.open("", "", "width=900,height=1100");
  if (!w) return;
  w.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>Invoice ${billNo}</title>
    <style>${styles}</style>
  </head>
  <body>${html}</body>
</html>`);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 100);
}

/* Build the same invoice artifact from a saved sales-invoice row (the shape
   returned by v1/sales). Used by the Invoices > Ready to bill screen, which
   has the persisted invoice but not the original order/lots context. */
export function buildInvoiceFromSales(invoice, overrides = {}) {
  if (!invoice) return null;
  const raw = (invoice.items || []).map((it, i) => {
    const qty = num(it.quantity);
    /* `sellingPrice` / `mrp` are stored PER STRIP. When the line was billed by
       the loose UNIT, `quantity` counts units, so the rate has to come down to
       per-unit first — otherwise MRP Total is unitsPerStrip times too big (a
       5-unit line off a ₹21.41 strip of 10 printed ₹107.05 instead of ₹10.71,
       which the Round Off line then papered over with -96.34).
       Left unrounded on purpose: computeInvoiceMath rounds mrp x qty, so
       21.41/10 x 5 = 10.705 -> 10.71, matching the backend's totalAmount. */
    const perStrip =
      String(it.unitOfMeasure || "").toUpperCase() === "UNIT" &&
      num(it.unitsPerStrip) > 0
        ? num(it.unitsPerStrip)
        : 1;
    const mrp = num(it.mrp) / perStrip;
    const gstPct = num(it.gstPercentage ?? it.gstRate ?? 5);
    const lineNet = num(it.amount); // final line net incl. GST, as billed by the backend
    /* Discounts come straight from the backend per-item fields (not re-derived).
       The item "Disc." column shows the full per-line reduction = product
       discount + CureCoin redemption. */
    const prodDiscAmt = num(it.productDiscountAmount);
    const cureCoinDisc = num(it.cureCoinDiscount);
    const prodDisc = round2(prodDiscAmt + cureCoinDisc);
    return {
      sno: i + 1,
      name: withRack(it.medicineName || "", it.rackCode),
      schedule: it.schedule || it.scheduleType || "",
      hsn: it.hsnCode || "",
      batch: it.batchNo || "",
      exp: formatExpMMYY(it.expiryDate),
      gstPct,
      /* Authoritative per-item GST the backend billed (v1/sales item.gstAmount) —
         drives the per-HSN GST summary below. */
      gstAmt: num(it.gstAmount),
      /* Authoritative per-item taxable value (v1/sales item.taxableAmount).
         null, not 0, when the backend omits it — 0 is a legitimate value and
         must not be mistaken for "absent" by the fallback below. */
      taxableAmt: it.taxableAmount == null ? null : round2(it.taxableAmount),
      mrp,
      prodDisc, // item Disc column = product discount + CureCoin
      prodDiscAmt, // product discount only (for the totals block breakdown)
      membershipDisc: 0,
      cureCoinDisc,
      qty,
      amount: lineNet,
      membershipDiscount: num(it.membershipDiscount),
      cureCoinDiscount: cureCoinDisc,
    };
  });
  if (raw.length === 0) return null;

  const pharmacy = resolvePharmacy();
  const {
    items: computedItems,
    totals: computedTotals,
    gstSummary: computedGst,
  } = computeInvoiceMath(raw);

  /* Trust the backend's authoritative money. The saved sales invoice already
     carries the posted totals (totalAmount / discountAmount / gstAmount /
     netAmount), so the reprint must render them verbatim — the client re-derive
     (computeInvoiceMath, MRP-inclusive) uses a different tax model and drifts
     from what the backend billed. See SUPPLY_BACKEND_MIGRATION.md. */
  const hasAuthTotals = [
    invoice.totalAmount,
    invoice.gstAmount,
    invoice.netAmount,
  ].some((v) => v != null);
  const gstAmt = round2(invoice.gstAmount);
  const netAmt = round2(invoice.netAmount);
  const totalMrp = computedTotals.totalMrp;
  /* Taxable value comes from the backend (v1/sales taxableAmount), like every
     other figure in this block.

     It must NOT be derived as `MRP total − GST`: GST is levied on the discounted
     net, not on MRP, so on any discounted bill that overstates the taxable value
     and the GST summary then foots to the MRP instead of to Net Payable. On the
     ₹192.80 / 10%-off example it printed 184.54 where the backend billed 165.26,
     making the tax invoice disagree with itself.

     The fallback works off the gross consideration, not the cash collected:
     CureCoin is spent as payment, so the taxable supply still includes it. On a
     ₹96.36 bill settled with ₹96.00 of coins the cash net is ₹0.36, and
     `net − GST` would go negative and clamp to 0 — printing a zero taxable value
     against ₹4.59 of tax. Adding the redemption back gives 91.77, which is what
     the backend bills. */
  const taxableAmt =
    invoice.taxableAmount != null
      ? round2(invoice.taxableAmount)
      : Math.max(0, round2(netAmt + num(invoice.cureCoinDiscount) - gstAmt));
  /* CGST and SGST are printed as equal halves: their rates are equal under the
     CGST/SGST Acts, so the two must match on the face of the invoice.

     KNOWN CONSEQUENCE — do not "fix" this by using `gstAmt - cgstAmt`: when
     gstAmt carries an odd paisa the two rounded halves sum to a paisa MORE than
     the backend's gstAmount (₹1.81 -> 0.91 + 0.91 = 1.82), so taxable + tax
     overshoots Net Payable by ₹0.01 and the bill's tax total will not tie to the
     filed figure. That is an accepted trade for equal halves.

     The clean way out is upstream: have pharmacy-ims round the half and double
     it (gstAmount = 2 x round2(taxable x rate/200)) so gstAmount is always an
     even number of paise. Then equal halves and exact footing both hold. */
  const cgstAmt = round2(gstAmt / 2);
  const sgstAmt = cgstAmt;
  /* Discounts from the backend's authoritative HEADER figures. `discountAmount`
     is the normal (product) discount only — it no longer bundles CureCoin, which
     the backend now reports separately as `cureCoinDiscount`. So the bill foots
     as: netAmount = totalAmount − discountAmount − cureCoinDiscount, shown as the
     two rows (Product Discount / CureCoin Discount) in the totals block.
     Older invoices whose header carries no discount fall back to per-item sums. */
  const headerHasDiscount =
    num(invoice.discountAmount) > 0 || num(invoice.cureCoinDiscount) > 0;
  const productDiscount = headerHasDiscount
    ? round2(num(invoice.discountAmount))
    : round2(raw.reduce((s, r) => s + num(r.prodDiscAmt), 0));
  const cureCoinDiscount = headerHasDiscount
    ? round2(num(invoice.cureCoinDiscount))
    : round2(raw.reduce((s, r) => s + num(r.cureCoinDisc), 0));
  const netDiscount = round2(productDiscount + cureCoinDiscount);

  /* Restate each line's taxable/CGST/SGST off the backend figures (its own
     taxableAmount, gst split in half) so the item table matches the GST summary
     and the totals block. Same basis as taxableAmt above — never MRP − gst. */
  const lineTaxableOf = (r) =>
    r.taxableAmt != null
      ? r.taxableAmt
      : Math.max(
          0,
          round2(num(r.amount) + num(r.cureCoinDisc) - num(r.gstAmt)),
        );

  const items = hasAuthTotals
    ? computedItems.map((it, idx) => {
        const r = raw[idx];
        /* Equal halves, same policy as the totals block above. */
        const half = round2(num(r.gstAmt) / 2);
        return {
          ...it,
          taxable: lineTaxableOf(r),
          cgst: half,
          sgst: half,
        };
      })
    : computedItems;

  const totals = hasAuthTotals
    ? {
        itemCount: items.length,
        totalMrp,
        productDiscount,
        membershipDiscount: 0,
        cureCoinDiscount,
        netDiscount,
        taxableValue: taxableAmt,
        cgst: cgstAmt,
        sgst: sgstAmt,
        /* Total MRP − Net Discount ± Round Off = Net Payable. */
        roundOff: round2(netAmt - (totalMrp - netDiscount)),
        netPayable: netAmt,
      }
    : computedTotals;
  /* Per-HSN GST summary from the backend's authoritative per-item tax
     (item.gstAmount / gstPercentage), grouped by HSN so repeated HSNs show as a
     single row with their shared rate. Falls back to the client-derived summary
     for older invoices whose items carry no gstAmount. */
  const buildBackendGst = () => {
    const groups = new Map();
    raw.forEach((r) => {
      /* Same basis as the totals block and the item table. */
      const lineTaxable = lineTaxableOf(r);
      const key = String(r.hsn);
      const g = groups.get(key) || {
        hsn: r.hsn,
        rate: r.gstPct,
        taxable: 0,
        gst: 0,
        mixed: false,
      };
      if (g.rate !== r.gstPct) g.mixed = true;
      g.taxable = round2(g.taxable + lineTaxable);
      g.gst = round2(g.gst + num(r.gstAmt));
      groups.set(key, g);
    });
    return [...groups.values()].map((g) => {
      /* Equal halves, same policy as the totals block. */
      const half = round2(g.gst / 2);
      return {
        hsn: g.hsn,
        rate:
          g.mixed && g.taxable > 0
            ? Math.round((g.gst / g.taxable) * 100)
            : g.rate,
        taxable: g.taxable,
        cgst: half,
        sgst: half,
      };
    });
  };
  const hasLineGst = raw.some((r) => num(r.gstAmt) > 0);
  let gstSummary;
  if (hasAuthTotals && hasLineGst) {
    const bg = buildBackendGst();
    /* A single HSN snaps to the bill-level tax so the summary foots exactly to
       the totals block; multiple HSNs keep their per-group backend figures. */
    gstSummary =
      bg.length === 1
        ? [{ ...bg[0], taxable: taxableAmt, cgst: cgstAmt, sgst: sgstAmt }]
        : bg;
  } else if (hasAuthTotals && computedGst.length === 1) {
    gstSummary = [
      { ...computedGst[0], taxable: taxableAmt, cgst: cgstAmt, sgst: sgstAmt },
    ];
  } else {
    gstSummary = computedGst;
  }

  const billNo = invoice.number || generateBillNo(invoice.id);

  const joinAddr = (...parts) => parts.filter(Boolean).join(", ");
  const patient = {
    id: invoice.patientId || invoice.customerId || "—",
    name: (invoice.customerName || "—").toUpperCase(),
    mobile: invoice.customerPhone || "—",
    age: invoice.patientAge || "—",
    gender: invoice.patientGender || "—",
    address:
      invoice.customerAddress ||
      joinAddr(invoice.address1, invoice.address2, invoice.address3),
  };

  const shipTo = {
    name: invoice.cheBranchName || "",
    address:
      invoice.deliveryAddress ||
      joinAddr(
        invoice.deliveryAddress1,
        invoice.deliveryAddress2,
        invoice.deliveryAddress3,
      ),
    mobile: "",
  };

  /* Payment transaction id — prefer the value persisted on the sales invoice
     itself (v1/sales response). The order-list lookup (overrides.txnId) is only
     a fallback for older invoices that pre-date the backend storing it, so it
     no longer overrides the authoritative value returned by the API. */
  const transactionId = (
    invoice.transactionId ||
    invoice.refTxnId ||
    overrides.txnId ||
    ""
  ).toString();

  const meta = {
    invoiceNo: billNo,
    invoiceDateText: formatLongDateTime(invoice.invoiceDate || new Date()),
    /* A split bill names every tender ("Store credit ₹340.00 + UPI ₹160.00");
       the header mode alone is only the largest of them. */
    paymentMode:
      Array.isArray(invoice.payments) && invoice.payments.length > 1
        ? invoice.payments
            .map(
              (p) =>
                `${paymentModeLabel(p.paymentMode)} ₹${num(p.amount).toFixed(2)}`,
            )
            .join(" + ")
        : paymentModeLabel(invoice.paymentMode),
    orderRef: String(
      invoice.orderId || invoice.number || `INV${invoice.id || ""}`,
    ).toUpperCase(),
    orderId: String(invoice.orderId || ""),
    transactionId,
    placeOfSupply: pharmacy.stateName
      ? `${pharmacy.stateName}${
          pharmacy.stateCode ? ` (State Code: ${pharmacy.stateCode})` : ""
        }`
      : "—",
  };

  const html = buildHTML({
    pharmacy,
    patient,
    shipTo,
    meta,
    items,
    totals,
    gstSummary,
    amountWords: amountInWords(totals.netPayable),
  });
  return { html, styles: STYLES, billNo, items, totals };
}

/* Build the same invoice artifact for a manual counter sale (Sales > Bill
   Entry). Unlike buildInvoice (which reconstructs from order/lots/alloc) the
   cashier's screen has already computed each line's net and its discount split,
   so we map those `computeBill` rows straight into the invoice math. The Disc.
   column therefore reflects the FULL per-line reduction (MRP markdown + the
   cashier's manual Disc%), kept consistent with the Net it produced.

   `rows` are computeBill(...).rows; each has { l, qty, mrp, prodDisc, memberDisc,
   coinDisc, net } where `l` is the entered line (medicineName, batch, exp, gst).
   `overrides` pins the backend's invoiceNumber / invoiceDate / txnId after the
   bill is posted, exactly like buildInvoice. */
export function buildCounterSaleInvoice({
  rows,
  customer = {},
  paymentMode,
  overrides = {},
  pharmacy = resolvePharmacy(),
}) {
  const raw = (rows || []).map((r, i) => ({
    sno: i + 1,
    name: withRack(r.l?.medicineName || "", r.l?.rack),
    schedule: "",
    /* HSN from the picked batch (per-lot). Left blank when the lot carries none
       — see the note on the allocation path above. */
    hsn: r.l?.hsn || r.l?.hsnCode || "",
    batch: r.l?.batch || "",
    exp: formatExpMMYY(r.l?.exp),
    /* Prefer the backend-priced GST rate (r.rate = pl.gstRate); fall back to the
       entered line rate only when pricing isn't available. */
    gstPct: num(r.rate) || num(r.l?.gst),
    mrp: num(r.mrp),
    /* Disc. column = MRP markdown + cashier Disc% (computeBill's prodDisc). */
    prodDisc: round2(num(r.prodDisc)),
    membershipDisc: round2(num(r.memberDisc)),
    cureCoinDisc: round2(num(r.coinDisc)),
    qty: num(r.qty),
    /* Net for the line after every discount — drives taxable / GST / payable. */
    amount: round2(num(r.net)),
  }));
  if (raw.length === 0) return null;

  const { items, totals, gstSummary } = computeInvoiceMath(raw);
  const billNo = overrides.billNo || generateBillNo();

  const patient = {
    id: "—",
    name: (customer.name || "Counter Sale").toUpperCase(),
    mobile: customer.mobile || "—",
    age: "—",
    gender: "—",
    address: customer.address || "",
  };
  /* Walk-in sale: no separate consignee — Ship To mirrors Bill To (buildHTML
     falls back to the patient when shipTo is omitted). */

  const meta = {
    invoiceNo: billNo,
    invoiceDateText: formatLongDateTime(overrides.billDateTime || new Date()),
    paymentMode: paymentMode || "Counter Sale",
    orderRef: (overrides.orderRef || billNo).toString().toUpperCase(),
    transactionId: (overrides.txnId || "").toString(),
    placeOfSupply: pharmacy.stateName
      ? `${pharmacy.stateName}${
          pharmacy.stateCode ? ` (State Code: ${pharmacy.stateCode})` : ""
        }`
      : "—",
  };

  const html = buildHTML({
    pharmacy,
    patient,
    meta,
    items,
    totals,
    gstSummary,
    amountWords: amountInWords(totals.netPayable),
  });
  return { html, styles: STYLES, billNo, items, totals };
}

/* Legacy single-shot: build + auto-print. Still used as a safety fallback. */
export default function printInvoice(args) {
  const built = buildInvoice(args);
  if (!built) return;
  openPrintWindow(built);
}
